import { ObjectValues } from '@app/types';
import { ShippingMethod } from '@app/types/cart/ShippingMethod';
import { IntlShape } from '@redbubble/next/intl';
import { shippingMethodLabels } from '../../ShippingOptions/messages';
import { StripeCart } from '../types';
import { getSubunitAmount } from '../utils';

type ShippingRate = {
  id: ObjectValues<typeof ShippingMethod>;
  displayName: string;
  amount: number;
};

export const createStandardRate = (
  cart: StripeCart,
  intl: IntlShape
): ShippingRate => {
  const amount = cart.summary.estimatedShipping.standard;
  return {
    id: ShippingMethod.Standard,
    displayName: intl.formatMessage(
      shippingMethodLabels[ShippingMethod.Standard]
    ),
    amount: getSubunitAmount(amount),
  };
};

export const createExpressRate = (
  cart: StripeCart,
  intl: IntlShape
): ShippingRate | null => {
  const express = cart.summary.estimatedShipping.express;

  if (!express) {
    return null;
  }

  return {
    id: ShippingMethod.Express,
    displayName: intl.formatMessage(
      shippingMethodLabels[ShippingMethod.Express]
    ),
    amount: getSubunitAmount(express),
  };
};

export const reorderRates = (
  rates: ShippingRate[],
  cart: StripeCart
): ShippingRate[] => {
  const selectedShipping =
    cart.summary.estimatedShipping.preference?.toUpperCase();
  // The first entry in the rates array is the default selected shipping option within the GooglePay/ApplePay modal.
  if (rates[0].id === selectedShipping) {
    return rates;
  }

  const selectedRate = rates.find((rate) => rate.id === selectedShipping);
  if (!selectedRate) {
    return rates;
  }

  const nonSelectedRates = rates.filter((rate) => rate.id !== selectedRate.id);

  return [selectedRate, ...nonSelectedRates];
};

export const fromCart = (cart: StripeCart, intl: IntlShape): ShippingRate[] => {
  const rates: ShippingRate[] = [];

  rates.push(createStandardRate(cart, intl));

  const expressRate = createExpressRate(cart, intl);
  if (expressRate) {
    rates.push(expressRate);
  }

  return reorderRates(rates, cart);
};
